/**
 * EmergencyOrdersTab.js — Emergency counselor order creation and management
 * CR-007 E-3: Counselor/System role can submit orders outside cycle window
 */
import React, { useState, useEffect, useCallback } from "react";
import S from "./adminStyles";
import { ORDERS_API, CATALOG_API } from "./adminConstants";

const REASON_OPTIONS = [
  { value: "urgent_need", label: "Urgent Need" },
  { value: "replacement", label: "Replacement Order" },
  { value: "other", label: "Other" },
];

const STATUS_LABELS = {
  pending_fulfillment: "⏳ Pending Fulfillment",
  fulfilled: "✅ Fulfilled",
  picked_up: "📦 Picked Up",
  not_picked_up: "⚠️ Not Picked Up",
  cancelled: "❌ Cancelled",
};

export default function EmergencyOrdersTab({ user }) {
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [message, setMessage] = useState(null);

  // Form state
  const [tokenId, setTokenId] = useState("");
  const [reason, setReason] = useState("urgent_need");
  const [selectedItems, setSelectedItems] = useState({});

  // Load catalog on mount
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const res = await fetch(CATALOG_API);
        if (res.ok) {
          const data = await res.json();
          setCatalog(data.sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.displayName || a.name || "").localeCompare(b.displayName || b.name || "")));
        }
      } catch (err) {
        console.error("Catalog load error:", err);
      } finally {
        setCatalogLoading(false);
      }
    };
    loadCatalog();
  }, []);

  // Load emergency orders
  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${ORDERS_API}/list?cycleId=emergency`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const emergencyOrders = (data.orders || data || [])
          .filter((o) => o.type === "emergency" || o.cycleId === "emergency")
          .sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || ""));
        setOrders(emergencyOrders);
      }
    } catch (err) {
      console.error("Emergency orders load error:", err);
    } finally {
      setOrdersLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Item quantity change handler
  const handleQtyChange = (itemId, qty, maxQty) => {
    const parsed = parseInt(qty) || 0;
    if (parsed === 0) {
      const next = { ...selectedItems };
      delete next[itemId];
      setSelectedItems(next);
    } else {
      setSelectedItems({ ...selectedItems, [itemId]: Math.min(parsed, maxQty) });
    }
  };

  // Submit emergency order
  const handleSubmit = async () => {
    setMessage(null);
    if (!tokenId.trim()) {
      setMessage({ type: "error", text: "Token ID is required" });
      return;
    }
    const items = Object.entries(selectedItems)
      .filter(([, qty]) => qty > 0)
      .map(([itemId, qty]) => ({ itemId, qty }));
    if (items.length === 0) {
      setMessage({ type: "error", text: "Select at least one item" });
      return;
    }

    setSubmitting(true);
    try {
      const authToken = await user.getIdToken();
      const res = await fetch(`${ORDERS_API}/emergency`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ tokenId: tokenId.trim(), items, reason }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: "success", text: `Emergency order ${data.orderId} created successfully` });
        setTokenId("");
        setSelectedItems({});
        setReason("urgent_need");
        loadOrders();
      } else {
        setMessage({ type: "error", text: data.error || "Failed to create emergency order" });
      }
    } catch (err) {
      console.error("Emergency order submit error:", err);
      setMessage({ type: "error", text: "Network error — please try again" });
    } finally {
      setSubmitting(false);
    }
  };

  // Update order status
  const handleStatusUpdate = async (orderId, newStatus) => {
    setStatusUpdating(orderId);
    try {
      const authToken = await user.getIdToken();
      const res = await fetch(`${ORDERS_API}/${orderId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        loadOrders();
      } else {
        const data = await res.json();
        setMessage({ type: "error", text: data.error || "Failed to update status" });
      }
    } catch (err) {
      console.error("Status update error:", err);
    } finally {
      setStatusUpdating(null);
    }
  };

  // Group catalog by category for display
  const categorizedCatalog = catalog.reduce((acc, item) => {
    const cat = item.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div>
      {/* Create Emergency Order Section */}
      <h3 style={{ marginBottom: 16 }}>🚨 Create Emergency Order</h3>

      {message && (
        <div style={{
          padding: "10px 14px",
          marginBottom: 16,
          borderRadius: 6,
          backgroundColor: message.type === "error" ? "#fce4ec" : "#e8f5e9",
          color: message.type === "error" ? "#c62828" : "#2e7d32",
          border: `1px solid ${message.type === "error" ? "#ef9a9a" : "#a5d6a7"}`,
        }}>
          {message.text}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Token ID (e.g., RD-RW-2026-01)"
          value={tokenId}
          onChange={(e) => setTokenId(e.target.value)}
          style={{ ...S.input, flex: "1 1 220px", minWidth: 220 }}
        />
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{ ...S.input, flex: "0 1 200px", minWidth: 160 }}
        >
          {REASON_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      {/* Catalog Item Selection */}
      {catalogLoading ? (
        <p>Loading catalog...</p>
      ) : (
        <div style={{ maxHeight: 350, overflowY: "auto", border: "1px solid #ddd", borderRadius: 6, padding: 12, marginBottom: 16 }}>
          {Object.entries(categorizedCatalog).map(([category, items]) => (
            <div key={category} style={{ marginBottom: 12 }}>
              <strong style={{ display: "block", marginBottom: 6, color: "#1a3a1a" }}>{category}</strong>
              {items.map((item) => {
                const maxQty = item.maxQtyPerOrder || 10;
                const currentQty = selectedItems[item.id] || 0;
                return (
                  <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                    <span style={{ flex: 1, fontSize: 14 }}>{item.displayName || item.name || item.id}</span>
                    <span style={{ fontSize: 12, color: "#888", minWidth: 60 }}>max: {maxQty}</span>
                    <input
                      type="number"
                      min={0}
                      max={maxQty}
                      value={currentQty}
                      onChange={(e) => handleQtyChange(item.id, e.target.value, maxQty)}
                      style={{ width: 60, textAlign: "center", padding: "4px 6px", borderRadius: 4, border: "1px solid #ccc" }}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Selected items summary */}
      {Object.keys(selectedItems).length > 0 && (
        <div style={{ marginBottom: 16, padding: 10, backgroundColor: "#f5f5f5", borderRadius: 6 }}>
          <strong>Selected: </strong>
          {Object.entries(selectedItems).map(([itemId, qty]) => {
            const item = catalog.find((c) => c.id === itemId);
            return `${item?.displayName || item?.name || itemId} (${qty})`;
          }).join(", ")}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={{ ...S.btn, backgroundColor: "#c62828", color: "#fff", padding: "10px 24px", fontSize: 15, opacity: submitting ? 0.6 : 1 }}
      >
        {submitting ? "Submitting..." : "🚨 Submit Emergency Order"}
      </button>

      {/* Emergency Orders List */}
      <hr style={{ margin: "32px 0 16px" }} />
      <h3 style={{ marginBottom: 16 }}>Emergency Order History</h3>

      {ordersLoading ? (
        <p>Loading emergency orders...</p>
      ) : orders.length === 0 ? (
        <p style={{ color: "#888" }}>No emergency orders found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Date</th>
                <th style={S.th}>Token</th>
                <th style={S.th}>Reason</th>
                <th style={S.th}>Items</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.orderId}>
                  <td style={S.td}>{order.submittedAt ? new Date(order.submittedAt).toLocaleDateString() : "—"}</td>
                  <td style={S.td}><code>{order.tokenId}</code></td>
                  <td style={S.td}>{REASON_OPTIONS.find((r) => r.value === order.reason)?.label || order.reason || "—"}</td>
                  <td style={S.td}>
                    {(order.items || []).map((i) => `${i.name || i.itemId} (${i.qty})`).join(", ")}
                  </td>
                  <td style={S.td}>{STATUS_LABELS[order.status] || order.status}</td>
                  <td style={S.td}>
                    {order.status === "pending_fulfillment" && (
                      <button
                        onClick={() => handleStatusUpdate(order.orderId, "fulfilled")}
                        disabled={statusUpdating === order.orderId}
                        style={{ ...S.btn, fontSize: 12, padding: "4px 10px" }}
                      >
                        {statusUpdating === order.orderId ? "..." : "Mark Fulfilled"}
                      </button>
                    )}
                    {order.status === "fulfilled" && (
                      <button
                        onClick={() => handleStatusUpdate(order.orderId, "picked_up")}
                        disabled={statusUpdating === order.orderId}
                        style={{ ...S.btn, fontSize: 12, padding: "4px 10px" }}
                      >
                        {statusUpdating === order.orderId ? "..." : "Mark Picked Up"}
                      </button>
                    )}
                    {["pending_fulfillment", "fulfilled"].includes(order.status) && (
                      <button
                        onClick={() => handleStatusUpdate(order.orderId, "cancelled")}
                        disabled={statusUpdating === order.orderId}
                        style={{ ...S.btn, fontSize: 12, padding: "4px 10px", marginLeft: 6, backgroundColor: "#fff", color: "#c62828", border: "1px solid #c62828" }}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
